import { useState, useEffect } from 'react';
import { RefreshCw, X, Download } from 'lucide-react';

export default function PWAUpdatePrompt() {
  const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    let refreshing = false;

    // Quand le nouveau SW prend le contrôle → recharger la page
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!refreshing) {
        refreshing = true;
        window.location.reload();
      }
    });

    const handleRegistration = (reg: ServiceWorkerRegistration) => {
      // SW déjà en attente au chargement
      if (reg.waiting) {
        setWaitingWorker(reg.waiting);
        setShow(true);
        return;
      }

      // Nouveau SW en cours d'installation
      reg.addEventListener('updatefound', () => {
        const newWorker = reg.installing;
        if (!newWorker) return;
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            setWaitingWorker(newWorker);
            setShow(true);
          }
        });
      });

      // Vérifier les mises à jour toutes les 60 s
      setInterval(() => {
        if (navigator.onLine) reg.update();
      }, 60_000);
    };

    navigator.serviceWorker.getRegistration().then(reg => {
      if (reg) handleRegistration(reg);
    });
  }, []);

  const applyUpdate = () => {
    if (waitingWorker) {
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    }
    setShow(false);
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] w-[calc(100%-2rem)] max-w-sm">
      <div
        className="flex items-center gap-3 px-4 py-3.5 rounded-2xl shadow-2xl border"
        style={{
          background: 'linear-gradient(135deg, #1e2a4a 0%, #0d1322 100%)',
          borderColor: 'rgba(99,102,241,0.35)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.45), 0 0 0 1px rgba(99,102,241,0.2)',
        }}
      >
        {/* Icône */}
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: 'rgba(99,102,241,0.2)', border: '1px solid rgba(99,102,241,0.3)' }}
        >
          <Download size={16} className="text-indigo-400" />
        </div>

        {/* Texte */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white leading-tight">
            Mise à jour disponible
          </p>
          <p className="text-[11px] text-slate-400 mt-0.5 leading-tight">
            Une nouvelle version de K-Prod est prête.
          </p>
        </div>

        {/* Bouton mettre à jour */}
        <button
          onClick={applyUpdate}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold text-white transition-all shrink-0"
          style={{
            background: 'linear-gradient(135deg, #6366f1, #4338ca)',
            boxShadow: '0 2px 8px rgba(99,102,241,0.4)',
          }}
        >
          <RefreshCw size={12} />
          Mettre à jour
        </button>

        {/* Fermer */}
        <button
          onClick={() => setShow(false)}
          className="p-1.5 rounded-lg text-slate-500 hover:text-slate-300 hover:bg-white/[0.07] transition-colors shrink-0"
          title="Ignorer"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
